import { Injectable } from '@angular/core';

import { CalculatedOdds } from '@frontend/sports/odds/feature';
import { Store } from '@ngrx/store';
import { Observable, map, tap, withLatestFrom } from 'rxjs';

import { isAccaBoostTokenSelector } from '../../../../lib/acca-boost-base/acca-boost-utils';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipReturnNotification } from '../../../digital/services/betslip-returns-messages-notification.service';
import { BetslipBarModel } from '../../betslip-bar/services/betslip-bar.service';
import { betslipPicksListSelector } from '../../picks/selectors';
import { IBetslipSummary } from '../../summary/models';
import { BetslipSummaryService } from '../../summary/services/betslip-summary.service';
import { selectQuickBetContext, selectQuickBetFreebetToken, selectQuickBetLegsCount } from '../quick-bet.selectors';
import { QuickBetRendererService } from './quick-bet-renderer.service';
import { QuickBetReturnsMessageService } from './quick-bet-returns-message.service';

export type QuickBetHeaderViewModel = Pick<BetslipBarModel, 'returnsMessage' | 'hasBoostedOdds' | 'isMulti' | 'pickCount' | 'comboBetPickCount'> & {
    accaBoostNotification?: BetslipReturnNotification;
};

@Injectable()
export class QuickBetHeaderService {
    private readonly selectedPickCount$ = this.store.select(selectQuickBetLegsCount);

    private readonly accaBoost$ = this.store.select(isAccaBoostTokenSelector);
    private readonly allPicks$ = this.store.select(betslipPicksListSelector);
    private readonly currentType$ = this.store.select(selectQuickBetContext);
    private readonly freeBetToken$ = this.store.select(selectQuickBetFreebetToken);

    readonly model$: Observable<QuickBetHeaderViewModel> = this.summaryService.getQuickBetHeaderSummary$().pipe(
        withLatestFrom(this.allPicks$, this.accaBoost$, this.selectedPickCount$, this.currentType$, this.freeBetToken$),
        map(([[, summary], allPicks, hasAccaBoost, pickCount, currentType, freeBetToken]) => {
            const boostedOdds = this.tryGetBoostedOdds(summary);

            return {
                returnsMessage: this.quickBetReturnsService.getReturnsMessage(
                    summary,
                    allPicks.length,
                    hasAccaBoost,
                    currentType.betslipType,
                    freeBetToken,
                ),
                odds: boostedOdds ?? summary.totalOdds,
                hasBoostedOdds: !!boostedOdds,
                pickCount,
                isMulti: allPicks.length > 1,
            };
        }),
        tap(() => (this.quickBetRendererService.summaryCalculationFinished = true)),
    );

    constructor(
        private store: Store<IBetslipRootState>,
        private summaryService: BetslipSummaryService,
        private quickBetReturnsService: QuickBetReturnsMessageService,
        private quickBetRendererService: QuickBetRendererService,
    ) {}

    private tryGetBoostedOdds({ slipSummaries = [] }: IBetslipSummary): CalculatedOdds | undefined {
        return slipSummaries[0]?.rewardTokenModifications?.oddsBoost?.boostedOdds;
    }
}
